import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, BOOKING_STATUS_LABELS, PAYMENT_STATUS_LABELS } from "@/lib/format";
import { MapPin, UserRound, CalendarCheck, PlusCircle, Eye, XCircle, Star, LifeBuoy, Download, Check, Phone } from "lucide-react";

export default function MiePrenotazioni() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["bookings"], queryFn: async () => (await api.get("/bookings")).data });
  const bookings = data?.bookings || [];
  const [detail, setDetail] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState("5");
  const [comment, setComment] = useState("");

  const cancel = async (id) => {
    try { await api.patch(`/bookings/${id}/cancel`, { reason: "Annullata dal paziente" }); toast.success("Prenotazione annullata."); qc.invalidateQueries({ queryKey: ["bookings"] }); setDetail(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.id, rating: Number(rating), comment });
      toast.success("Grazie per la recensione!"); setReviewFor(null); setComment(""); qc.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const acceptProposal = async (id) => {
    try { await api.post(`/bookings/${id}/accept-proposal`); toast.success("Orario alternativo accettato!"); qc.invalidateQueries({ queryKey: ["bookings"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadReceipt = async (id) => {
    try {
      const res = await api.get(`/bookings/${id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `ricevuta-${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Ricevuta scaricata.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Le mie prenotazioni">
      {isLoading ? <LoadingBlock /> : bookings.length === 0 ? (
        <EmptyState title="Nessuna prenotazione" subtitle="Le prenotazioni confermate appariranno qui."
          action={<Link to="/paziente/nuova-richiesta"><Button className="bg-[#087A73] text-white rounded-xl h-12 px-6"><PlusCircle className="mr-2 h-5 w-5" /> Nuova richiesta</Button></Link>} />
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <Card key={b.id} data-testid={`booking-row-${b.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="font-bold text-[#102A2A]">{b.service_name}</p>
                  <p className="text-sm text-[#5C6B68] flex items-center gap-1"><UserRound className="h-4 w-4" /> {b.professional_name}</p>
                  <p className="text-sm text-[#5C6B68] flex items-center gap-1"><CalendarCheck className="h-4 w-4" /> {formatDate(b.scheduled_date)} · {b.start_time}-{b.end_time}</p>
                  <p className="text-sm text-[#5C6B68] flex items-center gap-1"><MapPin className="h-4 w-4" /> {b.address}</p>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-2">
                  <StatusBadge status={b.booking_status} label={BOOKING_STATUS_LABELS[b.booking_status]} testid={`booking-status-${b.id}`} />
                  <span className="text-xs text-[#5C6B68]">{PAYMENT_STATUS_LABELS[b.payment_status]}</span>
                </div>
              </div>
              {b.proposed_date && b.booking_status !== "annullata" && (
                <div className="mt-3 bg-[#FBF0D6] border border-[#D99A00]/40 text-[#8a6400] rounded-xl px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <span>Il professionista propone un orario alternativo: <strong>{formatDate(b.proposed_date)} alle {b.proposed_time}</strong></span>
                  <Button data-testid={`accept-proposal-${b.id}`} onClick={() => acceptProposal(b.id)} className="bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-10 px-4 text-sm shrink-0"><Check className="mr-1 h-4 w-4" /> Accetta orario</Button>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button data-testid={`booking-detail-${b.id}`} variant="outline" onClick={() => setDetail(b)} className="rounded-xl h-10 px-4 border-2 border-[#D2E3DB] text-sm"><Eye className="mr-1 h-4 w-4" /> Vedi dettagli</Button>
                {["pagato", "demo_pagato"].includes(b.payment_status) && (
                  <Button data-testid={`booking-receipt-${b.id}`} variant="outline" onClick={() => downloadReceipt(b.id)} className="rounded-xl h-10 px-4 border-2 border-[#087A73] text-[#087A73] text-sm"><Download className="mr-1 h-4 w-4" /> Ricevuta PDF</Button>
                )}
                {b.booking_status === "confermata" && b.professional_phone && (
                  <a href={`tel:${b.professional_phone}`} data-testid={`call-pro-${b.id}`} className="inline-flex items-center rounded-xl h-10 px-4 border-2 border-[#087A73] text-[#087A73] text-sm font-semibold hover:bg-[#087A73]/10 transition-colors"><Phone className="mr-1 h-4 w-4" /> Chiama {b.professional_name.split(" ")[0]}</a>
                )}
                {["in_attesa_di_conferma", "confermata"].includes(b.booking_status) && (
                  <Button data-testid={`booking-cancel-${b.id}`} variant="outline" onClick={() => cancel(b.id)} className="rounded-xl h-10 px-4 border-2 border-[#C0392B]/30 text-[#C0392B] text-sm"><XCircle className="mr-1 h-4 w-4" /> Annulla</Button>
                )}
                {b.booking_status === "completata" && (
                  <Button data-testid={`booking-review-${b.id}`} onClick={() => setReviewFor(b)} className="rounded-xl h-10 px-4 bg-[#087A73] text-white text-sm"><Star className="mr-1 h-4 w-4" /> Recensisci</Button>
                )}
                <Link to="/paziente/assistenza"><Button variant="ghost" className="rounded-xl h-10 px-4 text-[#5C6B68] text-sm"><LifeBuoy className="mr-1 h-4 w-4" /> Assistenza</Button></Link>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>Dettaglio prenotazione</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-[#5C6B68]">Numero: <span className="font-mono font-bold text-[#102A2A]">{detail.id.slice(0, 8).toUpperCase()}</span></p>
              <Row l="Servizio" v={detail.service_name} />
              <Row l="Professionista" v={detail.professional_name} />
              <Row l="Data" v={`${formatDate(detail.scheduled_date)} · ${detail.start_time}-${detail.end_time}`} />
              <Row l="Indirizzo" v={detail.address} />
              <Row l="Riceve la visita" v={detail.recipient_name} />
              <Row l="Pagamento" v={PAYMENT_STATUS_LABELS[detail.payment_status]} />
              <div className="flex justify-between"><span className="text-[#5C6B68]">Stato</span><StatusBadge status={detail.booking_status} label={BOOKING_STATUS_LABELS[detail.booking_status]} /></div>
              <div className="bg-[#EAF3EF] rounded-xl p-3 text-[#5C6B68] text-xs">Il professionista potrà contattarti al numero indicato per informazioni organizzative.</div>
            </div>
          )}
          <DialogFooter>
            {detail && ["in_attesa_di_conferma", "confermata"].includes(detail.booking_status) && (
              <Button variant="outline" onClick={() => cancel(detail.id)} className="rounded-xl border-2 border-[#C0392B]/30 text-[#C0392B]">Annulla prenotazione</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* review dialog */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Lascia una recensione</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Valutazione</Label>
              <Select value={rating} onValueChange={setRating}>
                <SelectTrigger data-testid="review-rating" className="h-12 rounded-xl mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{"⭐".repeat(n)} ({n})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Commento</Label><Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} className="rounded-xl mt-1" placeholder="Racconta la tua esperienza (facoltativo)" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="review-submit" onClick={submitReview} className="bg-[#087A73] text-white rounded-xl h-11 px-6">Invia recensione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Row({ l, v }) {
  return <div className="flex justify-between gap-4"><span className="text-[#5C6B68]">{l}</span><span className="font-semibold text-[#102A2A] text-right">{v}</span></div>;
}
